'use client'
import { alert } from '@repo/ui/Alert'
import { confirm } from '@repo/ui/Confirm'
import { ContactsAddEditForm } from '@repo/contacts-organisations/ContactsAddEditForm'
import { ContactsOrganisationsList } from '@repo/contacts-organisations/ContactsOrganisationsList'
import { ContactsOrganisationsPreview } from '@repo/contacts-organisations/ContactsOrganisationsPreview'
import { CrossIcon } from '@repo/ui/CrossIcon'
import { EmptyState } from '@repo/ui/EmptyState'
import { LargeContactDetailsIcon } from '@repo/ui/LargeContactDetailsIcon'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useState, useEffect } from 'react'
import { useWindowSize } from '@uidotdev/usehooks'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import PropTypes from 'prop-types'

export const ContactsManager = ({
  preSelectExpertTypeId,
  onSelect = () => {},
  initialSelectedItem,
  sx = {},
  onEditModeChange = () => {},
  onCreateModeChange = () => {},
  testData
}) => {
  const mode = 'contacts'
  const { contactsById, refreshContactsTrigger, refreshOrganisationsTrigger, deleteContact, deleteOrganisation } =
    useLookupsStore()
  const { allowedActions } = useMenuItemsStore()
  const allowEditContacts = allowedActions?.editContacts?.allowed || false
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const [selectedItem, setSelectedItem] = useState(initialSelectedItem || null)
  const [contact, setContact] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const { height: windowHeight } = useWindowSize()

  useEffect(() => {
    if (selectedItem?.contactId && contactsById?.[selectedItem.contactId]) {
      setContact(contactsById[selectedItem.contactId])
      setShowCreateForm(false)
      onSelect(contactsById[selectedItem.contactId])
    }
  }, [selectedItem, contactsById, onSelect])

  useEffect(() => {
    if (selectedItem?.contactId && !contactsById?.[selectedItem?.contactId]) {
      setSelectedItem(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshContactsTrigger, refreshOrganisationsTrigger])

  useEffect(() => {
    onCreateModeChange(showCreateForm)
  }, [showCreateForm, onCreateModeChange])

  const displayCreateForm = () => {
    setSelectedItem(null)
    setContact(null)
    setShowCreateForm(true)
  }

  const handleNewContact = ({ contactId }) => {
    setSelectedItem({ contactId })
    setShowCreateForm(false)
  }

  const handleDelete = async (item, mode) => {
    const proceed = await confirm('Are you sure you want to delete this item?')
    if (!proceed) return

    if (mode === 'contacts') {
      onSelect(null)
      setContact(null)
      setSelectedItem(null)
      await deleteContact({ id: item.contactId })
    } else {
      try {
        await deleteOrganisation({ id: item.organisationId })
      } catch (error) {
        console.error(error)
        alert('We could not delete this organisation. It may be linked to a contact.')
      }
    }
  }

  const marginTop = pageTitleBlockIsVisible ? 0 : '140px'

  return (
    <MUIStack
      data-testid="ContactsManager"
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={'30px'}
      sx={{
        width: 'calc(100% - 80px)',
        maxWidth: '1200px',
        margin: '0 auto',
        marginTop,
        padding: '14px 30px',
        height: `calc(${windowHeight}px - 180px)`,
        minHeight: '500px',
        backgroundColor: 'white',
        borderRadius: '6px',
        border: '1px solid #D8DCE5',
        position: 'relative',
        ...sx
      }}
    >
      <ContactsOrganisationsList
        onSelect={setSelectedItem}
        selectedItem={selectedItem}
        mode={mode}
        preSelectExpertTypeId={preSelectExpertTypeId}
        testData={testData}
      />

      {allowEditContacts ? (
        <MUIStack
          direction="row"
          justifyContent="flex-end"
          data-testid="newButton"
          sx={{
            flex: 1,
            padding: '4px 0px',
            zIndex: 1,
            position: 'absolute',
            top: '16px',
            right: '30px'
          }}
        >
          <MUIButton variant="contained" color="primary" onClick={displayCreateForm} sx={{ width: '180px' }}>
            <CrossIcon sx={{ width: '18px', height: '18px', mr: 2, color: 'white', transform: 'rotate(45deg)' }} />
            New contact
          </MUIButton>
        </MUIStack>
      ) : null}

      <MUIStack
        direction="column"
        sx={{
          flex: 1,
          marginTop: '59px',
          overflowY: 'auto',
          backgroundColor: 'white',
          padding: '20px 0px',
          borderRadius: '6px',
          height: 'calc(100% - 64px)'
        }}
      >
        {!selectedItem && showCreateForm ? (
          <div style={{ padding: '0px 20px' }}>
            <ContactsAddEditForm onCancel={() => setShowCreateForm(false)} onSuccess={handleNewContact} />
          </div>
        ) : null}

        {!selectedItem && !showCreateForm ? (
          <EmptyState
            text="Select an item to see details"
            icon={<LargeContactDetailsIcon sx={{ mb: 8, width: '64px', height: '64px' }} />}
          />
        ) : null}

        {contact?.contactId ? (
          <ContactsOrganisationsPreview
            itemId={selectedItem?.contactId}
            mode="contacts"
            onEditModeChange={onEditModeChange}
            sx={{ mb: contact?.organisationId ? 12 : 0 }}
            handleDelete={handleDelete}
          />
        ) : null}

        {contact?.organisationId ? (
          <ContactsOrganisationsPreview
            itemId={contact.organisationId}
            mode="organisations"
            onEditModeChange={onEditModeChange}
            handleDelete={handleDelete}
          />
        ) : null}
      </MUIStack>
    </MUIStack>
  )
}

ContactsManager.propTypes = {
  preSelectExpertTypeId: PropTypes.any,
  onSelect: PropTypes.func,
  initialSelectedItem: PropTypes.any,
  sx: PropTypes.object,
  onEditModeChange: PropTypes.func,
  onCreateModeChange: PropTypes.func,
  testData: PropTypes.any
}
